import React, { useContext } from 'react'
import Address from './Address';
import DropDown from './DropDown';
import Options from './Options';
import { AzanCallOptions } from '../data/AzanCallOptions';
import { DeviceModes } from '../data/DeviceModes';
import { FajrAzans, Azans } from '../data/Audios';
import { CalculationMethods, AsrCalculationMethods } from '../data/CalculationMethods';
import { AppContext } from '../AppContext';
import { FontAwesome } from '../data/FontAwesome';
import { format12 } from '../scripts/SmartAzanClock';
import { Languages } from '../data/I18n';
import { DisplayThemes } from '../data/DisplayThemes';
import { DisplayLayouts } from '../data/DisplayLayouts';

export default function Settings() {

    const { vakits, arcVakits, calculationSettings, locationSettings, deviceSettings, azanSettings,
        offsetSettings, updateOffset, previewAudio, oneThirdTime, twoThirdTime, midnightTime, t } = useContext(AppContext)

    const CalculationMethodValues = [];
    Object.keys(CalculationMethods).forEach(k => {
        CalculationMethodValues.push({ id: k, name: CalculationMethods[k].name });
    })

    const isRamadan = new Intl.DateTimeFormat('en-u-ca-islamic', { month: 'numeric' }).format(new Date()) === '9';
    const azanEnabled = deviceSettings.azanCallsEnabled === 'Y';
    const YesNoOptions = AzanCallOptions.map(o => ({ ...o, name: t(o.id === 'Y' ? 'common.yes' : 'common.no') }));
    const LocalDeviceModes = DeviceModes.map(o => ({
        ...o,
        name: t(o.id === 'A' ? 'options.auto' : (o.id === 'D' ? 'options.dim' : 'options.normal'))
    }));
    const LocalAsrCalculationMethods = AsrCalculationMethods.map(o => ({
        ...o,
        name: t(o.id === 'H' ? 'options.hanafi' : 'options.standard')
    }));
    const LocalDisplayThemes = DisplayThemes.map(o => ({ ...o, name: t(o.nameKey) }));
    const LocalDisplayLayouts = DisplayLayouts.map(o => ({ ...o, name: t(o.nameKey) }));
    const azanSettingsHTML = [];
    const Vakits = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    Vakits.forEach((item) => {

        let cVakit = item.toLowerCase();
        let azanValue = azanSettings[cVakit];
        let offsetValue = offsetSettings[cVakit];
        let values = (item === "Fajr") ? FajrAzans : Azans;
        let vTime = vakits.find(v => v.name === item).displayTime;

        azanSettingsHTML.push(

            <div key={item} className="mt-2">

                <div className='d-flex flex-row justify-content-between'>
                    <div><span className='badge p-0'>{t('prayers.' + item)} {t('settings.azan')} {t('common.at')} {vTime}</span></div>
                    <div className='col-4'><span className='badge'>{t('settings.minuteOffset')}</span></div>
                </div>

                <div className='d-flex flex-row gap-1'>

                    <div className='col-6'>
                        <DropDown name={'azanSettings.' + cVakit} selectedValue={azanValue} values={values} disabled={!azanEnabled} />
                    </div>
                    <div className='col-2'>
                        <button onClick={() => { previewAudio(azanValue * 1); document.activeElement.blur(); }}
                            type='button'
                            disabled={!azanEnabled}
                            className='btn btn-sm btn-primary col-12'>{FontAwesome.Play}</button>
                    </div>
                    <div>
                        <div className='d-flex flex-row gap-1 align-items-center'>
                            <div className='col-4'><button type='button' onClick={() => { updateOffset(cVakit, '-'); document.activeElement.blur(); }} className='btn btn-sm btn-light col-12'>{FontAwesome.Minus}</button></div>
                            <div className='col-4'><button type='button' onClick={() => { updateOffset(cVakit, '0'); document.activeElement.blur(); }} className={'btn btn-sm col-12 ' + ((offsetValue === 0) ? 'btn-light' : 'btn-danger')}>{offsetValue}</button></div>
                            <div className='col-4'><button type='button' onClick={() => { updateOffset(cVakit, '+'); document.activeElement.blur(); }} className='btn btn-sm btn-light col-12'>{FontAwesome.Plus}</button></div>
                        </div>
                    </div>
                </div>

            </div >

        )
    })

    return (
        <div>

            {isRamadan && <div className='alert alert-warning p-2 mb-2' style={{ fontSize: '0.85rem' }}>
                <strong>{t('settings.ramadanTipTitle')}</strong> {t('settings.ramadanTipBody')}
            </div>}

            <Address value={locationSettings.address} />

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('common.language')}</span>
                <Options name="deviceSettings.language" selectedValue={deviceSettings.language || 'en'} values={Languages} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.calculationMethod')}</span>
                <DropDown name="calculationSettings.method" selectedValue={calculationSettings.method} values={CalculationMethodValues} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.asrCalculationMethod')}</span>
                <Options name="calculationSettings.asrMethod" selectedValue={calculationSettings.asrMethod} values={LocalAsrCalculationMethods} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.displayMode')}</span>
                <Options name="deviceSettings.mode" selectedValue={deviceSettings.mode} values={LocalDeviceModes} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('layouts.label')}</span>
                <Options name="deviceSettings.layout" selectedValue={deviceSettings.layout || 'clock'} values={LocalDisplayLayouts} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('themes.label')}</span>
                <Options name="deviceSettings.theme" selectedValue={deviceSettings.theme || 'classic'} values={LocalDisplayThemes} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.screenSaverMode')}</span>
                <Options name="deviceSettings.screenSaver" selectedValue={deviceSettings.screenSaver} values={YesNoOptions} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.zoomedInMode')}</span>
                <Options name="deviceSettings.zoomedIn" selectedValue={deviceSettings.zoomedIn} values={YesNoOptions} />
            </div>

            <div className="mt-2">
                <span className='badge mb-1 p-0'>{t('settings.enableAzanCalls')}</span>
                <Options name="deviceSettings.azanCallsEnabled" selectedValue={deviceSettings.azanCallsEnabled} values={YesNoOptions} />
            </div>

            {azanSettingsHTML}

            <div className='d-flex flex-row justify-content-start gap-2 mt-3'>
                <div className='badge bg-secondary p-1'>{t('settings.imsak')} {t('common.at')} {format12(arcVakits.find(f => f.name === 'Imsak').time)}</div>
                <div className='badge bg-secondary p-1'>{t('settings.duha')} {t('common.at')} {format12(arcVakits.find(f => f.name === 'Duha').time)} - {format12(arcVakits.find(f => f.name === 'Duhaend').time)}</div>
            </div>
            <div className='d-flex flex-row justify-content-start gap-2 mt-2'>
                <div className='badge bg-secondary p-1'>1/3 {t('common.at')} {format12(oneThirdTime)}</div>
                <div className='badge bg-secondary p-1'>{t('settings.midnight')} {t('common.at')} {format12(midnightTime)}</div>
                <div className='badge bg-secondary p-1'>2/3 {t('common.at')} {format12(twoThirdTime)}</div>
            </div>

        </div >
    )
}
